import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  DeviceMobile,
  Printer,
  Hand,
  Sliders,
  GridFour,
  ArrowsHorizontal,
  UserCircle,
  FileImage,
  AppleLogo,
  AndroidLogo,
  ForkKnife,
  User,
  Mountains,
  PersonSimpleWalk,
  Moon,
  MagnifyingGlassPlus,
  Package,
  Football,
  Sparkle,
  ImagesSquare,
  CheckCircle,
  Target,
  Rocket,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const CONTENT = {
  en: {
    page_title: "Smartphone Photography",
    page_sub: "Your phone is the camera you ALWAYS have with you. Modern iPhones and flagship Androids capture 12-50 MP images with computational HDR that rivals dedicated cameras for everyday work. Master these 6 fundamentals and your phone shots will stop looking like phone shots.",
    download: "Download as PDF",
    generating: "Generating...",
    ios_label: "iOS (iPhone)",
    android_label: "Android",
    pro_tip_label: "Pro tip",

    sections: [
      {
        id: "exposure",
        icon: "Hand",
        title: "1. Mastering Mobile Exposure & Focus",
        intro: "Every smartphone camera defaults to auto-everything. The single fastest upgrade you can make is taking control of focus and exposure separately — exactly like a professional camera.",
        ios_steps: [
          "Tap once on your subject. A yellow square appears = focus + auto-exposure locked on that point.",
          "A small SUN icon appears next to the square.",
          "Slide your finger UP on the sun icon = brighter exposure. Slide DOWN = darker.",
          "Tap-and-HOLD on the subject (1 second) until 'AE/AF LOCK' appears in yellow at the top. The focus and exposure are now LOCKED — you can recompose without the camera re-metering.",
          "Tap anywhere to release the lock.",
        ],
        android_steps: [
          "Open the stock camera app and tap on your subject. A focus reticle appears.",
          "Most Android phones (Samsung, Pixel, OnePlus) show a brightness/exposure SLIDER next to the focus point.",
          "Drag the slider UP for brighter, DOWN for darker.",
          "On Pixel and Samsung: tap-and-hold to lock both AE and AF — a yellow 'AE/AF LOCK' badge confirms.",
          "On stock Samsung: separate 'Pro' button gives a dedicated EV (exposure compensation) slider from -2.0 to +2.0 EV.",
        ],
        tip: "Train yourself to ALWAYS do this for backlit subjects, snowy/beachy scenes, and high-contrast portraits. It's the single biggest amateur-to-pro upgrade on a phone.",
      },
      {
        id: "pro",
        icon: "Sliders",
        title: "2. Using Pro Mode / Manual Controls",
        intro: "When you need full control — ISO, shutter speed, white balance, manual focus — your phone can do it. The default Camera app hides these controls; here's where to find them.",
        ios_steps: [
          "iOS Camera app does NOT include native Pro Mode. You need a third-party app.",
          "Recommended FREE: Halide (paid but excellent), VSCO, ProCam, Adobe Lightroom Mobile (camera tab).",
          "In Lightroom Mobile Camera: top toggle = Auto / Pro / HDR / Long Exposure / Depth.",
          "In Pro mode you control: ISO (32-3000+), Shutter (1/8000 to 30s), WB (Kelvin slider), Focus (manual + peaking), File format (JPG or DNG RAW).",
          "Capture format matters: enable 'DNG' to record RAW for full editing latitude.",
        ],
        android_steps: [
          "Most Android stock cameras have a built-in 'Pro' or 'Manual' mode — swipe the mode wheel.",
          "Samsung Galaxy: open Camera → swipe to 'More' → tap 'Pro'. Full ISO, Shutter, WB, MF controls.",
          "Pixel: stock camera is intentionally minimal — install 'Open Camera' (free) or 'Manual Camera' for full Pro controls.",
          "OnePlus: 'Pro' mode in stock camera. Same level of control as Samsung.",
          "Always shoot RAW (.DNG) in Pro mode for maximum editing flexibility in Lightroom or Snapseed.",
        ],
        tip: "Use Pro Mode for low-light scenes (lock ISO low, use longer shutter on a tripod), waterfalls and motion blur (1/4s - 2s), and any scene where Auto WB is fighting you (warm tungsten interiors).",
      },
      {
        id: "composition",
        icon: "GridFour",
        title: "3. Composition Rules on a Small Screen",
        intro: "On a 6-inch screen it's easy to slap your subject dead-center. Turn on Grid Lines and apply the Rule of Thirds in 5 seconds — your shots immediately look more deliberate and professional.",
        ios_steps: [
          "Settings app → Camera → Grid → toggle ON.",
          "A 3x3 grid now overlays the viewfinder.",
          "Place subjects on the intersection points (where lines cross), not in the center.",
          "Horizons go on the TOP or BOTTOM third line, never centered.",
          "Bonus: in same Settings → Camera, enable 'Mirror Front Camera' to make selfies feel natural, and 'View Outside the Frame' to preview what wider lenses would capture.",
        ],
        android_steps: [
          "Stock Camera app → Settings (gear icon) → Grid lines → enable '3x3' or 'Rule of Thirds'.",
          "Samsung: Camera → Settings (gear) → Grid lines → 3x3.",
          "Pixel: Camera → Settings → 'More settings' → 'Framing hints' → Grid type.",
          "Use the same composition rules: subjects on intersections, horizons on the top/bottom third line.",
          "Bonus on Pixel: 'Level' toggle adds a horizon line that turns yellow when your phone is straight.",
        ],
        tip: "Beyond Rule of Thirds: use the grid to spot crooked horizons (most amateur shots have a slight tilt). The 'Level' indicator on Pixel and Samsung cameras is your friend.",
      },
      {
        id: "lenses",
        icon: "ArrowsHorizontal",
        title: "4. Lens Selection (Wide / Telephoto / Ultra-Wide)",
        intro: "Modern flagships ship with 2-4 PHYSICAL lenses. Switching between them is real optical zoom — sharp. Digital zoom (pinch beyond the longest lens) is software-stretched garbage — avoid it.",
        ios_steps: [
          "iPhone Pro models: tap '.5x' for ultra-wide (~13mm equivalent), '1x' for main wide (~24mm), '2x'/'3x'/'5x' for telephoto (varies by model — iPhone 15 Pro Max has 5x).",
          "Tap the zoom labels at the bottom — DO NOT pinch-zoom past the longest physical lens, that's digital zoom and quality collapses.",
          "Use .5x ultra-wide for: landscapes, tight interiors, group photos, dramatic perspective.",
          "Use 1x for: 99% of everyday photos, street, food, environmental portraits.",
          "Use 2x-5x telephoto for: tight portraits (flattering compression), distant subjects, candid moments without intruding.",
        ],
        android_steps: [
          "Samsung Ultra / Pixel Pro: tap '.6x' / '1x' / '3x' / '5x' / '10x' physical lens buttons. Pinch only switches lenses up to the longest physical zoom.",
          "Avoid '30x' / '100x Space Zoom' marketing — those use heavy AI upscaling and aren't true optical.",
          "Ultra-wide (.5x or .6x): same uses as iPhone — landscapes, interiors, groups.",
          "Main wide (1x): the SHARPEST lens on every phone. Use for important shots when in doubt.",
          "Telephoto (3x-10x): compression for portraits, isolating distant subjects, candids.",
        ],
        tip: "The rule: if you need to zoom past your phone's longest physical lens, MOVE CLOSER instead. Walking 6 feet forward produces a sharper photo than 'digital zoom' every single time.",
      },
      {
        id: "portrait",
        icon: "UserCircle",
        title: "5. Portrait Mode & Artificial Depth of Field",
        intro: "Portrait Mode uses computational AI + depth sensors to FAKE the shallow depth of field that a real f/1.4 lens creates naturally. When it works, it's magic. When you misuse it, the fake bokeh has obvious edge errors. Here's how to use it well.",
        ios_steps: [
          "Camera app → swipe modes at the bottom → select 'PORTRAIT'.",
          "Stay 2-8 feet from your subject. Too close = camera shows 'Move Farther Away'. Too far = depth detection fails.",
          "Choose a lighting style: Natural / Studio / Contour / Stage / High-Key Mono — these add computational lighting effects in addition to background blur.",
          "Tap the f-icon (top right). Slide from f/1.4 (extreme blur) to f/16 (almost no blur). You can adjust this AFTER taking the photo, in the Photos app.",
          "Best subjects: people (face detection is most accurate), pets, isolated objects on a clean background.",
        ],
        android_steps: [
          "Samsung: Camera → swipe to 'Portrait' mode. Adjust blur strength with the slider (0-7 levels).",
          "Pixel: 'Portrait' mode in stock camera. Use Google's depth AI for both people AND objects/pets.",
          "Standoff: 2-8 feet. Hold the phone steady — Portrait mode requires more processing and a tiny shake = soft edges.",
          "On Pixel: after capture, open the photo → Edit → 'Tools' → 'Blur' — you can RETROACTIVELY adjust the bokeh amount.",
          "Samsung: in Gallery → Edit → 'Background Effect' to re-adjust blur strength after capture.",
        ],
        tip: "Portrait Mode struggles with: glasses (edge halos), fine hair (often blurred incorrectly), pet whiskers, busy patterned backgrounds. For these, shoot in regular mode and let the natural distance + telephoto lens do the bokeh work.",
      },
      {
        id: "raw",
        icon: "FileImage",
        title: "6. Mobile Editing & RAW Capture",
        intro: "JPEG locks in the camera's color, exposure, and contrast decisions FOREVER. RAW (.DNG on Android, ProRAW on iPhone) preserves all the sensor data so you can fix exposure, white balance, and color in post — without quality loss. Combined with a free mobile editor, you have a desktop-grade darkroom in your pocket.",
        ios_steps: [
          "Settings → Camera → Formats → 'Most Compatible' OR 'High Efficiency' OFF; then scroll down to 'Apple ProRAW' → toggle ON. (iPhone 12 Pro and newer only.)",
          "In the Camera app, tap 'RAW' or 'RAW MAX' at the top of the viewfinder to enable RAW capture for that shot.",
          "ProRAW files are huge (~25 MB each) — keep RAW ON only when shooting important stuff, not casual snaps.",
          "Edit in: Lightroom Mobile (free with Adobe ID), Snapseed (free, by Google), Darkroom (free + paid pro).",
          "Lightroom Mobile workflow: open RAW → adjust Exposure / Highlights / Shadows / Whites / Blacks → fix White Balance with the eyedropper on something neutral → export as JPG for sharing.",
        ],
        android_steps: [
          "Samsung: Camera → 'Pro' mode → tap the file-format icon → select 'RAW'. (Saves both JPG + DNG side-by-side.)",
          "Pixel: stock camera → Settings → 'Advanced' → 'RAW + JPEG control' → ON.",
          "OnePlus / Xiaomi: enable RAW under Pro mode settings.",
          "RAW DNG files are universal — they open in Lightroom Mobile, Snapseed, Photoshop Express, and even Darkroom.",
          "Quick edit workflow in Snapseed: open RAW → 'Tools' → 'Tune Image' for exposure/contrast → 'White Balance' for color → 'Selective' to brighten specific areas with a finger drag.",
        ],
        tip: "Always shoot RAW for: golden hour, sunsets, sunrises, indoor portraits with mixed light, snowy landscapes, ANY high-contrast scene. For casual social media snaps where you'll post immediately, JPEG is fine.",
      },
    ],
    closing: "Your phone is not a 'less than' camera — it is a different camera, optimized for portability and computational intelligence. Master these 6 techniques and you will routinely produce phone shots that hold their own next to dedicated cameras for everything but extreme low light, wildlife telephoto, and large-print fine art.",

    upgrade_title: "Ready to Upgrade? When a Phone Isn't Enough",
    upgrade_sub: "Master phone photography first — it's the fastest, cheapest way to fall in love with the craft. But at some point your eye will outgrow what computational AI can deliver. Here are the 5 scenarios where a real DSLR or mirrorless camera dramatically outperforms even the best phone, and what to do next.",
    upgrade_scenarios: [
      {
        scene: "Extreme low light & astrophotography",
        why: "Phone sensors are physically tiny (~1/1.3 inch on a Pixel Pro). A full-frame DSLR sensor is ~30x larger. That sensor area gathers ~30x more light per shot — making star trails, the Milky Way, dim concert venues, and indoor sports at night actually possible without smeary AI noise reduction.",
      },
      {
        scene: "Wildlife & sports telephoto",
        why: "A phone's longest physical lens tops out around 5x optical (~120mm equivalent). A mid-range DSLR + 100-400mm zoom reaches 400mm natively — 8x more reach with NO quality loss. For birds, distant athletes, or skittish animals, this gap is unbridgeable.",
      },
      {
        scene: "Large fine-art prints",
        why: "Phones produce great 4x6 and 8x10 prints. But a 24-inch gallery print exposes every bit of computational AI smoothing, every smeared corner of an ultra-wide, every artifact of Night Mode. Real cameras shoot true 24-45 MP files that hold up to billboard-size enlargement.",
      },
      {
        scene: "True shallow depth of field (real f/1.4 bokeh)",
        why: "Portrait Mode FAKES bokeh with AI. Real f/1.4 glass on a full-frame body creates physical, perfect, edge-error-free background blur — even on glasses, fine hair, and busy backgrounds where Portrait Mode collapses. Once you see real bokeh, the computational version looks plasticky.",
      },
      {
        scene: "Sports tracking & fast action",
        why: "Modern mirrorless cameras (Sony A9 III, Canon R3, Nikon Z9) shoot 20-40 fps with subject-tracking AI that follows an athlete's eye across the frame. Phones max out around 30 fps with no continuous focus tracking. For surfers, race cars, basketball, or anything moving fast and erratically, this is night-and-day.",
      },
    ],
    upgrade_cta_title: "Your next steps",
    upgrade_cta_steps: [
      { title: "1. Use the DSLR Settings Advisor", desc: "75+ camera models supported — pick the body you're considering and explore real exposure recommendations for the scenes you want to shoot.", to: "/", btn: "Open Advisor" },
      { title: "2. Read 'Camera Body Decision' on Careers", desc: "What separates entry-level, mid-range, and pro bodies — and which makes sense for YOUR style.", to: "/careers", btn: "Read Careers" },
      { title: "3. Plan your kit with My Gear", desc: "Inventory tool lets you track camera + lens + lighting + accessories with purchase prices, serial numbers, and an Excel export for insurance.", to: "/gear", btn: "Open My Gear" },
      { title: "4. Visit the Shots Library", desc: "Every project you tried on a phone, planned with a real camera + recommended settings. See what's possible when you cross that bridge.", to: "/shots", btn: "See Shots" },
    ],
    upgrade_closing: "Phone → DSLR is the most natural progression in photography. You already have the EYE. A real camera just removes the technical ceiling.",

    projects_title: "10 Practice Projects — Shoot, Self-Critique, Share",
    projects_sub: "Now apply what you learned. Each project is designed for a smartphone and includes the technique to practice, exact settings to start with, 5 tips, deliverables, and a self-critique checklist so you (or a friend) can review the result honestly.",
    difficulty_label: "Difficulty",
    objective_label: "Objective",
    settings_label: "Starting Settings",
    tips_label: "Tips",
    deliverables_label: "Deliverables",
    critique_label: "Self-Critique Checklist",
    diff_beginner: "Beginner",
    diff_intermediate: "Intermediate",
    diff_advanced: "Advanced",

    projects: [
      {
        id: "phone_food",
        icon: "ForkKnife",
        title: "1. Food Photography (Window Light)",
        desc: "Photograph a meal using only window light — practice composition and tap-to-focus on the small screen.",
        difficulty: "beginner",
        relates: "Lesson 1 (Exposure & Focus), Lesson 3 (Composition)",
        objective: "5 images of a plated dish from different angles (overhead, 45-degree, eye-level) using ONLY window light.",
        settings: "1x main lens | Grid lines ON | Tap subject to focus → slide sun-slider DOWN slightly to protect highlights | Shoot RAW if available",
        tips: [
          "Place the dish 60-90cm from a large window. The light should hit the plate from the side, not behind you.",
          "Use a white napkin or paper plate as a fill reflector on the shadow side.",
          "Tap on the brightest part of the dish to expose for it — then recompose.",
          "Try the 45-degree 'human eating perspective' first, then go overhead (flat lay), then eye-level for hero shot.",
          "Avoid overhead room lights — they make food look orange/yellow. Window light only.",
        ],
        deliverables: "5 edited images: 1 overhead flat-lay, 2 at 45°, 1 eye-level hero, 1 detail close-up.",
        critique: [
          "Does the food look APPETIZING (not flat or shadowy)?",
          "Are the highlights on the food preserved (not blown out white)?",
          "Is the plate in sharp focus (especially the closest edge)?",
          "Did you crop tightly — no distracting clutter in the frame?",
          "Did the rule of thirds place the dish off-center, not dead center?",
        ],
      },
      {
        id: "phone_portrait",
        icon: "User",
        title: "2. Portrait — 3 Styles, Same Person",
        desc: "Three distinct portraits using your phone's main lens, telephoto lens, AND Portrait Mode.",
        difficulty: "beginner",
        relates: "Lesson 4 (Lens Selection), Lesson 5 (Portrait Mode)",
        objective: "Produce 3 portraits of the SAME person showing different moods + different lens choices.",
        settings: "Portrait 1: 1x main | Portrait 2: 2x or 3x telephoto | Portrait 3: Portrait Mode at 2x | Tap-and-hold to AE/AF Lock on the eye",
        tips: [
          "Shoot in open shade (no direct sun on the face — squinting and harsh shadows).",
          "Focus on the eye closer to the camera. Tap and hold on the eye until AE/AF Lock appears.",
          "For Portrait Mode: stay 2-8 feet away. Watch for ear/glasses halos and adjust if needed.",
          "Telephoto lens (2x/3x) flattens features = more flattering than the 1x for tight portraits.",
          "After capture, in Portrait Mode go Edit → adjust f-stop slider to taste.",
        ],
        deliverables: "3 portraits: 1 tight headshot (telephoto), 1 environmental (1x with surroundings), 1 Portrait Mode with computed bokeh.",
        critique: [
          "Are the eyes RAZOR sharp? (zoom in at 100%)",
          "Is the skin tone natural — not orange, green, or magenta?",
          "Did Portrait Mode handle hair and glasses cleanly, or are there obvious halo errors?",
          "Did you use the telephoto for the tight headshot (NOT the wide)?",
          "Does each of the 3 portraits feel DIFFERENT in mood or perspective?",
        ],
      },
      {
        id: "phone_landscape",
        icon: "Mountains",
        title: "3. Golden Hour Landscape (Ultra-Wide)",
        desc: "Capture a golden hour landscape using your phone's .5x ultra-wide lens.",
        difficulty: "beginner",
        relates: "Lesson 4 (Lens Selection), Lesson 6 (RAW Capture)",
        objective: "5 landscape images shot during golden hour using ONLY the .5x ultra-wide lens. RAW capture mandatory.",
        settings: ".5x ultra-wide lens | Grid lines ON | RAW (.DNG) format enabled | Tap to focus on a midground element | Brace against something solid (rocks, railing) for stability",
        tips: [
          "Arrive 30 minutes before sunset to scout. Golden hour lasts ~45 minutes max.",
          "Include a STRONG foreground element 1-3 feet from the lens — flowers, rocks, water. Ultra-wide exaggerates foreground.",
          "Place the horizon on the lower third (sky-dominant) OR upper third (land-dominant). Never dead center.",
          "Tap to focus 1/3 into the scene. Slide exposure DOWN slightly to protect the bright sky.",
          "Edit the RAW DNG in Lightroom Mobile: lift shadows +30, drop highlights -30, increase clarity.",
        ],
        deliverables: "5 edited landscapes: 2 wide vistas, 1 with strong foreground, 1 with leading lines (road, river), 1 vertical 9:16 for Instagram Stories.",
        critique: [
          "Is the horizon level (use the grid + level indicator)?",
          "Are the highlights in the sky preserved (not pure white)?",
          "Is there a clear foreground element giving the photo depth?",
          "Did you avoid the dead-center horizon trap?",
          "Did you EDIT the RAW, or just export the JPG? Real edits show in shadows and color.",
        ],
      },
      {
        id: "phone_street",
        icon: "PersonSimpleWalk",
        title: "4. Street Photography (Candid)",
        desc: "Capture 5 candid moments of strangers in a public space. The phone's invisibility is a superpower here.",
        difficulty: "intermediate",
        relates: "Lesson 1 (Exposure), Lesson 4 (Lens Selection)",
        objective: "5 candid street photos showing real human moments — no posed shots.",
        settings: "1x main lens OR 2x telephoto | AE/AF Lock on a midtone wall BEFORE you raise the phone to shoot | Burst mode for moving subjects",
        tips: [
          "Pre-lock exposure on a midtone wall in the same light as your subject. Then raise the phone, frame, and tap shutter — no metering delay = no lost moment.",
          "Hold the phone at chest height (not face height) for less obvious shooting.",
          "Telephoto lens (2x/3x) lets you shoot from across the street — far less intrusive.",
          "Look for layers: foreground person + midground event + background context.",
          "Respect the law: in most places it's legal to shoot in public spaces, but follow local norms and never harass anyone.",
        ],
        deliverables: "5 candid street images: at least 2 with people, 1 with strong shadows or light pattern, 1 architectural detail, 1 reflection or layered scene.",
        critique: [
          "Do the photos feel CANDID (people doing real things, not posing)?",
          "Is the moment captured at the peak of action / expression?",
          "Did you avoid awkward crops at joints (elbows, knees)?",
          "Is the exposure consistent across the set (you used pre-locked AE)?",
          "Would a stranger seeing this set understand the LOCATION and the MOOD?",
        ],
      },
      {
        id: "phone_lowlight",
        icon: "Moon",
        title: "5. Low-Light / Night Mode",
        desc: "Push your phone's computational AI to the limit — shoot 5 scenes after dark using Night Mode.",
        difficulty: "intermediate",
        relates: "Lesson 2 (Pro Mode), Lesson 6 (RAW)",
        objective: "5 night images: 2 cityscape, 2 portrait, 1 long exposure of moving lights.",
        settings: "iPhone: Night Mode auto-activates in low light, hold still for 1-3 seconds | Android: Pixel 'Night Sight', Samsung 'Night' mode | Brace phone against a wall or rail for stability",
        tips: [
          "Night Mode takes 3-10 seconds. You MUST hold the phone PERFECTLY still — brace it against something solid.",
          "Lock exposure BEFORE Night Mode triggers: tap and hold on the brightest part of the scene.",
          "For light-trail long exposures (cars, traffic), use Pro Mode with 4-15 second shutter on a tripod or solid brace.",
          "For night portraits, have the subject stand still for 2-3 seconds while Night Mode processes.",
          "Avoid the 1x at the longest digital zoom — noise destroys the shot. Stick to optical lenses only at night.",
        ],
        deliverables: "5 night images: 2 cityscapes, 2 night portraits, 1 light-trail or star scene.",
        critique: [
          "Is the image SHARP (not blurry from motion)?",
          "Is noise / grain acceptable, or did you over-zoom?",
          "Are the highlights from streetlights preserved (not glowing blobs)?",
          "Did the night portrait freeze the subject (not motion-blurred)?",
          "Does it feel like nighttime (not over-brightened into looking like day)?",
        ],
      },
      {
        id: "phone_macro",
        icon: "MagnifyingGlassPlus",
        title: "6. Macro / Close-Up",
        desc: "Use your phone's macro mode (or get within 5 cm) to capture detail invisible to the naked eye.",
        difficulty: "intermediate",
        relates: "Lesson 1 (Focus), Lesson 4 (Lenses)",
        objective: "5 macro images: a flower, a textured surface, an insect or food detail, water droplets, and a creative everyday object.",
        settings: "iPhone 13 Pro+: Macro auto-engages when you get within 14 cm | Other phones: install a free macro app or use the ultra-wide at minimum focus distance | Tap PRECISELY on the focus point you want sharp | Burst mode helps",
        tips: [
          "DOF is paper-thin in macro. Focus precisely on the most important detail (insect eye, flower center).",
          "Use natural light, never on-board LED flash — flash creates ugly hard shadows at close range.",
          "Brace your elbows or rest the phone on something. Even tiny shake destroys macro sharpness.",
          "Spray water droplets on flowers or leaves with a spray bottle for added drama.",
          "Edit aggressively: macro shots benefit from +clarity, +texture, and selective sharpening on the focus point.",
        ],
        deliverables: "5 macro photos showing: 1 flower, 1 texture, 1 living thing OR food, 1 water droplet, 1 creative object.",
        critique: [
          "Is the FOCAL POINT (insect eye, flower stamen, droplet) razor sharp?",
          "Does the shallow depth of field draw the viewer's eye to the subject?",
          "Is the background clean / non-distracting?",
          "Are colors and textures vivid, not muddy?",
          "Could a viewer guess what the subject IS without context?",
        ],
      },
      {
        id: "phone_product",
        icon: "Package",
        title: "7. Product Photography (E-Commerce Style)",
        desc: "Photograph 3 small products for an online store. Sharp, accurate color, clean background.",
        difficulty: "intermediate",
        relates: "Lesson 2 (Pro Mode + WB), Lesson 6 (RAW)",
        objective: "9 photos total (3 products × 3 angles each): a hero shot, a 3/4 angle, and a detail close-up.",
        settings: "Pro Mode | Manual WB at ~5500K (daylight) | ISO 50-100 | f/2.8 (or just main lens) | Tripod or solid brace mandatory | RAW format",
        tips: [
          "Use a white poster board or printer paper as both background AND fill reflector.",
          "Shoot in soft window light (early morning or late afternoon). Avoid direct sun.",
          "Set Manual White Balance — don't trust Auto WB on a clean product shoot.",
          "Use the telephoto lens (2x/3x) for less perspective distortion on small objects.",
          "Edit RAW: remove dust spots, fix WB precisely with the eyedropper, sharpen the product edges.",
        ],
        deliverables: "9 edited photos: 3 hero, 3 angled, 3 detail shots — all with consistent white background and color.",
        critique: [
          "Is the product color ACCURATE (matches the real-life color)?",
          "Is the background pure white or near-white (use the white sampler in Lightroom)?",
          "Are reflections / hot spots controlled, not distracting?",
          "Is the product razor sharp where it counts?",
          "Could you ACTUALLY use these on a Shopify or Etsy listing?",
        ],
      },
      {
        id: "phone_action",
        icon: "Football",
        title: "8. Action / Sports (Burst Mode)",
        desc: "Capture a moving subject — kids playing, a pet running, a skateboarder, a sports game.",
        difficulty: "intermediate",
        relates: "Lesson 1 (Focus), Lesson 4 (Lens)",
        objective: "5 frozen-action images using Burst Mode and the telephoto lens.",
        settings: "Telephoto lens (2x/3x) for reach | Hold the shutter button to fire burst (up to 30+ frames) | AE/AF Lock on the subject's path BEFORE starting | Outdoors in bright light is easiest",
        tips: [
          "Pre-focus on the spot the action will pass through, then shoot in burst when it arrives.",
          "Bright daylight = fast shutter speeds → frozen motion. Indoor sports are MUCH harder.",
          "Telephoto + burst = a phone's sports superpower. The 1x is too wide for distant action.",
          "Track the subject through the burst — don't just spray-and-pray.",
          "From a 30-frame burst, pick the 1-2 frames with peak expression / peak action. Delete the rest.",
        ],
        deliverables: "5 best-of-burst action shots: 2 wide context, 3 tight peak-action.",
        critique: [
          "Is the action FROZEN (no motion blur on the subject)?",
          "Is the subject sharp — eyes / ball / focal point in focus?",
          "Did you pick the PEAK moment from each burst, not a mid-motion frame?",
          "Is the composition strong (subject not awkwardly cropped at joints)?",
          "Does the photo tell a story or convey energy?",
        ],
      },
      {
        id: "phone_creative",
        icon: "Sparkle",
        title: "9. Creative / Self-Portrait Series",
        desc: "Use your front camera, mirrors, reflections, and creative angles to make 5 SELF-PORTRAITS that don't look like selfies.",
        difficulty: "advanced",
        relates: "Lesson 3 (Composition), Lesson 5 (Portrait Mode)",
        objective: "5 self-portraits using different techniques — at least one must use a mirror, one must use Portrait Mode, one must use a long shadow or silhouette.",
        settings: "Front camera OR rear (better quality) with timer | Tripod, gimbal, or stable surface | Self-timer 10s + Burst | Grid lines ON",
        tips: [
          "REAR camera quality is always better than front. Use the timer + tripod for a pro-look self-portrait.",
          "Mirrors double your composition. Shoot yourself reflected in window glass, lake water, or a car mirror.",
          "Silhouettes: position yourself in front of a bright sky/window. Underexpose -2 EV.",
          "Long shadows during golden hour: shoot your own shadow on a wall or ground from a low angle.",
          "Get creative with mirrors, framing through objects, doorways, leaves, etc. Avoid the obvious bathroom-mirror selfie.",
        ],
        deliverables: "5 self-portraits, each with a different technique. Add 1-line caption explaining your concept for each.",
        critique: [
          "Does each photo feel DIFFERENT — different mood, technique, or visual idea?",
          "Did you avoid the cliche 'bathroom mirror selfie'?",
          "Are you in FOCUS (not the mirror frame or background)?",
          "Is the lighting flattering or intentional?",
          "Could a stranger guess your CONCEPT for each photo?",
        ],
      },
      {
        id: "phone_series",
        icon: "ImagesSquare",
        title: "10. Visual Story — 24 Hours in My Life",
        desc: "A culminating project. Document a single day with 10 phone photos that form a coherent visual narrative.",
        difficulty: "advanced",
        relates: "All 6 lessons combined",
        objective: "10 photos chronicling 24 hours of your life. Must include: morning, midday, evening, and night. Different lenses, different modes, different moods.",
        settings: "Apply EVERYTHING you learned: tap to focus + AE/AF lock for high-contrast scenes, RAW for important shots, Portrait Mode for one or two, Pro Mode for night, telephoto for candids, ultra-wide for environments.",
        tips: [
          "Plan a story arc: wake up → routine → outdoor activity → meal → evening → night.",
          "Mix wide environmental shots with intimate detail close-ups for visual rhythm.",
          "Include AT LEAST one shot from each of the 6 lessons: focus/exposure, Pro Mode, composition (grid), lens variety, Portrait Mode, RAW edit.",
          "Edit consistently — apply the same color treatment to all 10 in Lightroom Mobile for a cohesive look.",
          "Caption each photo with a sentence. The captions form a written narrative alongside the visual one.",
        ],
        deliverables: "10 edited photos in chronological order + a 1-paragraph reflection on what you learned about your day AND about phone photography.",
        critique: [
          "Do the 10 photos tell a CLEAR story arc (morning to night)?",
          "Is there visual variety (wide / tight, light / dark, portraits / details)?",
          "Did you use AT LEAST 4 different techniques from the 6 lessons?",
          "Does the editing feel CONSISTENT across all 10 (same color tone, same crop ratio if appropriate)?",
          "If someone scrolled this without captions, would they UNDERSTAND your day?",
        ],
      },
    ],
  },
  es: {
    page_title: "Fotografia con Smartphone",
    page_sub: "Tu telefono es la camara que SIEMPRE llevas contigo. Los iPhones modernos y los Android tope de gama capturan imagenes de 12-50 MP con HDR computacional que rivaliza con camaras dedicadas para trabajo diario. Domina estos 6 fundamentos y tus tomas dejaran de verse como tomas de telefono.",
    download: "Descargar como PDF",
    generating: "Generando...",
    ios_label: "iOS (iPhone)",
    android_label: "Android",
    pro_tip_label: "Tip pro",

    sections: [
      {
        id: "exposure",
        icon: "Hand",
        title: "1. Dominar la Exposicion y el Enfoque Movil",
        intro: "Cada camara de smartphone defaultea a auto-todo. La mejora mas rapida que puedes hacer es tomar control del enfoque y la exposicion por separado — exactamente como una camara profesional.",
        ios_steps: [
          "Toca una vez sobre tu sujeto. Aparece un cuadrado amarillo = enfoque + auto-exposicion bloqueados en ese punto.",
          "Un pequeño icono de SOL aparece junto al cuadrado.",
          "Desliza el dedo HACIA ARRIBA sobre el sol = exposicion mas brillante. HACIA ABAJO = mas oscuro.",
          "Toca-y-MANTEN sobre el sujeto (1 segundo) hasta que aparezca 'BLOQUEO AE/AF' en amarillo arriba. El enfoque y la exposicion estan BLOQUEADOS — puedes recomponer sin que la camara remida.",
          "Toca en cualquier lugar para liberar el bloqueo.",
        ],
        android_steps: [
          "Abre la app de Camara stock y toca tu sujeto. Aparece un reticulo de enfoque.",
          "La mayoria de Androids (Samsung, Pixel, OnePlus) muestran un SLIDER de brillo/exposicion junto al punto de enfoque.",
          "Arrastra el slider HACIA ARRIBA para mas brillo, ABAJO para mas oscuro.",
          "En Pixel y Samsung: toca-y-manten para bloquear AE y AF — una insignia amarilla 'BLOQUEO AE/AF' lo confirma.",
          "En Samsung stock: el boton 'Pro' separado da un slider EV dedicado (compensacion de exposicion) de -2.0 a +2.0 EV.",
        ],
        tip: "Entrenate a SIEMPRE hacer esto para sujetos a contraluz, escenas nevadas/playa, y retratos de alto contraste. Es la mejora amateur-a-pro mas grande en un telefono.",
      },
      {
        id: "pro",
        icon: "Sliders",
        title: "2. Usar Modo Pro / Controles Manuales",
        intro: "Cuando necesitas control total — ISO, velocidad, balance de blancos, enfoque manual — tu telefono puede hacerlo. La app de Camara por defecto esconde estos controles; aqui es donde encontrarlos.",
        ios_steps: [
          "La app de Camara de iOS NO incluye Modo Pro nativo. Necesitas una app de terceros.",
          "Recomendadas GRATIS: Halide (de paga pero excelente), VSCO, ProCam, Adobe Lightroom Mobile (pestaña de camara).",
          "En Lightroom Mobile Camera: toggle superior = Auto / Pro / HDR / Larga Exposicion / Profundidad.",
          "En modo Pro controlas: ISO (32-3000+), Velocidad (1/8000 a 30s), WB (slider Kelvin), Enfoque (manual + peaking), Formato (JPG o DNG RAW).",
          "El formato de captura importa: activa 'DNG' para grabar RAW con maxima latitud de edicion.",
        ],
        android_steps: [
          "La mayoria de Androids stock tienen modo 'Pro' o 'Manual' integrado — desliza la rueda de modos.",
          "Samsung Galaxy: abre Camara → desliza a 'Mas' → toca 'Pro'. Controles completos de ISO, Velocidad, WB, MF.",
          "Pixel: la camara stock es intencionalmente minimalista — instala 'Open Camera' (gratis) o 'Manual Camera' para controles Pro completos.",
          "OnePlus: modo 'Pro' en camara stock. Mismo nivel de control que Samsung.",
          "Dispara siempre RAW (.DNG) en modo Pro para maxima flexibilidad de edicion en Lightroom o Snapseed.",
        ],
        tip: "Usa el Modo Pro para escenas de poca luz (bloquea ISO bajo, velocidad larga sobre tripode), cascadas y desenfoque de movimiento (1/4s - 2s), y cualquier escena donde el Auto WB te pelee (interiores tungsteno calidos).",
      },
      {
        id: "composition",
        icon: "GridFour",
        title: "3. Reglas de Composicion en Pantalla Pequeña",
        intro: "En una pantalla de 6 pulgadas es facil meter al sujeto justo al centro. Activa las Lineas de Cuadricula y aplica la Regla de los Tercios en 5 segundos — tus tomas se ven inmediatamente mas deliberadas y profesionales.",
        ios_steps: [
          "App de Ajustes → Camara → Cuadricula → activa.",
          "Aparece una cuadricula 3x3 sobre el visor.",
          "Coloca sujetos en los puntos de interseccion (donde las lineas se cruzan), no en el centro.",
          "Los horizontes van en la linea de tercio SUPERIOR o INFERIOR, nunca centrados.",
          "Bonus: en mismos Ajustes → Camara, activa 'Reflejar Camara Frontal' para selfies naturales, y 'Ver Fuera del Encuadre' para previsualizar lo que captarian lentes mas amplios.",
        ],
        android_steps: [
          "App Camara stock → Ajustes (icono engranaje) → Lineas de cuadricula → activa '3x3' o 'Regla de Tercios'.",
          "Samsung: Camara → Ajustes → Lineas de cuadricula → 3x3.",
          "Pixel: Camara → Ajustes → 'Mas ajustes' → 'Sugerencias de encuadre' → tipo de cuadricula.",
          "Usa las mismas reglas de composicion: sujetos en intersecciones, horizontes en la linea de tercio superior/inferior.",
          "Bonus en Pixel: el toggle 'Nivel' añade una linea de horizonte que se vuelve amarilla cuando el telefono esta recto.",
        ],
        tip: "Mas alla de la Regla de Tercios: usa la cuadricula para detectar horizontes torcidos (la mayoria de tomas amateur tienen un leve ladeo). El indicador 'Nivel' en Pixel y Samsung es tu amigo.",
      },
      {
        id: "lenses",
        icon: "ArrowsHorizontal",
        title: "4. Seleccion de Lentes (Gran Angular / Telefoto / Ultra Gran Angular)",
        intro: "Los flagships modernos vienen con 2-4 lentes FISICOS. Cambiar entre ellos es zoom optico real — nitido. El zoom digital (pellizcar mas alla del lente mas largo) es basura estirada por software — evitalo.",
        ios_steps: [
          "iPhone Pro: toca '.5x' para ultra gran angular (~13mm equivalente), '1x' para gran angular principal (~24mm), '2x'/'3x'/'5x' para telefoto (varia por modelo — iPhone 15 Pro Max tiene 5x).",
          "Toca las etiquetas de zoom en la parte inferior — NO pellizques mas alla del lente fisico mas largo, eso es zoom digital y la calidad colapsa.",
          "Usa .5x ultra gran angular para: paisajes, interiores cerrados, fotos grupales, perspectiva dramatica.",
          "Usa 1x para: 99% de fotos diarias, calle, comida, retratos ambientales.",
          "Usa 2x-5x telefoto para: retratos cerrados (compresion favorecedora), sujetos lejanos, momentos candidos sin invadir.",
        ],
        android_steps: [
          "Samsung Ultra / Pixel Pro: toca los botones de lentes fisicos '.6x' / '1x' / '3x' / '5x' / '10x'. Pellizcar solo cambia entre lentes fisicos hasta el zoom mas largo.",
          "Evita el marketing '30x' / '100x Space Zoom' — usan upscaling pesado de IA y no son optico real.",
          "Ultra gran angular (.5x o .6x): mismos usos que iPhone — paisajes, interiores, grupos.",
          "Gran angular principal (1x): el lente MAS NITIDO en todo telefono. Usa para tomas importantes ante la duda.",
          "Telefoto (3x-10x): compresion para retratos, aislar sujetos lejanos, candidos.",
        ],
        tip: "La regla: si necesitas hacer zoom mas alla del lente fisico mas largo de tu telefono, MUEVETE MAS CERCA. Caminar 2 metros adelante produce una foto mas nitida que el 'zoom digital' cada vez.",
      },
      {
        id: "portrait",
        icon: "UserCircle",
        title: "5. Modo Retrato y Profundidad de Campo Artificial",
        intro: "El Modo Retrato usa IA computacional + sensores de profundidad para FALSIFICAR la profundidad de campo poco profunda que un lente f/1.4 real crea naturalmente. Cuando funciona, es magia. Cuando se mal usa, el bokeh falso tiene errores obvios en los bordes. Aqui esta como usarlo bien.",
        ios_steps: [
          "App Camara → desliza modos abajo → selecciona 'RETRATO'.",
          "Mantente a 60cm-2.4m del sujeto. Muy cerca = la camara muestra 'Alejate Mas'. Muy lejos = la deteccion de profundidad falla.",
          "Elige estilo de iluminacion: Natural / Estudio / Contorno / Escenario / Alto Tono Mono — añaden efectos de iluminacion computacional ademas del desenfoque de fondo.",
          "Toca el icono-f (arriba derecha). Desliza de f/1.4 (desenfoque extremo) a f/16 (casi sin desenfoque). Puedes ajustarlo DESPUES de tomar la foto, en la app Fotos.",
          "Mejores sujetos: personas (la deteccion facial es la mas precisa), mascotas, objetos aislados sobre fondo limpio.",
        ],
        android_steps: [
          "Samsung: Camara → desliza a modo 'Retrato'. Ajusta la fuerza del desenfoque con el slider (0-7 niveles).",
          "Pixel: modo 'Retrato' en camara stock. Usa la IA de profundidad de Google para personas Y objetos/mascotas.",
          "Distancia: 60cm-2.4m. Manten el telefono firme — el Modo Retrato requiere mas procesamiento y un pequeño movimiento = bordes suaves.",
          "En Pixel: despues de capturar, abre la foto → Editar → 'Herramientas' → 'Desenfoque' — puedes ajustar RETROACTIVAMENTE la cantidad de bokeh.",
          "Samsung: en Galeria → Editar → 'Efecto de Fondo' para reajustar la fuerza del desenfoque despues de capturar.",
        ],
        tip: "El Modo Retrato batalla con: lentes (halos en bordes), pelo fino (a menudo mal desenfocado), bigotes de mascotas, fondos con patrones ocupados. Para estos, dispara en modo normal y deja que la distancia natural + el telefoto hagan el trabajo de bokeh.",
      },
      {
        id: "raw",
        icon: "FileImage",
        title: "6. Edicion Movil y Captura RAW",
        intro: "JPEG bloquea las decisiones de color, exposicion y contraste de la camara PARA SIEMPRE. RAW (.DNG en Android, ProRAW en iPhone) preserva todos los datos del sensor para que arregles exposicion, balance de blancos y color en post — sin perdida de calidad. Combinado con un editor movil gratuito, tienes un cuarto oscuro de grado escritorio en tu bolsillo.",
        ios_steps: [
          "Ajustes → Camara → Formatos → 'Mas Compatible' O 'Alta Eficiencia' DESACTIVADO; luego baja a 'Apple ProRAW' → activa. (Solo iPhone 12 Pro y posteriores.)",
          "En la app Camara, toca 'RAW' o 'RAW MAX' arriba del visor para activar captura RAW en esa toma.",
          "Los archivos ProRAW son enormes (~25 MB cada uno) — manten RAW activo solo cuando dispares cosas importantes, no tomas casuales.",
          "Edita en: Lightroom Mobile (gratis con Adobe ID), Snapseed (gratis, de Google), Darkroom (gratis + pro de paga).",
          "Flujo en Lightroom Mobile: abre RAW → ajusta Exposicion / Altas Luces / Sombras / Blancos / Negros → arregla Balance de Blancos con el cuentagotas sobre algo neutro → exporta como JPG para compartir.",
        ],
        android_steps: [
          "Samsung: Camara → modo 'Pro' → toca el icono de formato → selecciona 'RAW'. (Guarda JPG + DNG lado a lado.)",
          "Pixel: camara stock → Ajustes → 'Avanzado' → 'Control RAW + JPEG' → activa.",
          "OnePlus / Xiaomi: activa RAW en ajustes del modo Pro.",
          "Los archivos RAW DNG son universales — abren en Lightroom Mobile, Snapseed, Photoshop Express, e incluso Darkroom.",
          "Flujo rapido en Snapseed: abre RAW → 'Herramientas' → 'Ajustar Imagen' para exposicion/contraste → 'Balance de Blancos' para color → 'Selectivo' para iluminar areas especificas arrastrando con el dedo.",
        ],
        tip: "Dispara siempre RAW para: golden hour, atardeceres, amaneceres, retratos interiores con luz mixta, paisajes nevados, CUALQUIER escena de alto contraste. Para tomas casuales de redes sociales que publicaras de inmediato, JPEG esta bien.",
      },
    ],
    closing: "Tu telefono no es una camara 'inferior' — es una camara DIFERENTE, optimizada para portabilidad e inteligencia computacional. Domina estas 6 tecnicas y produciras rutinariamente tomas con telefono que se sostienen junto a camaras dedicadas para todo menos baja luz extrema, telefoto de fauna silvestre, y bellas artes en impresion grande.",

    upgrade_title: "Listo para Mejorar? Cuando un Telefono No Es Suficiente",
    upgrade_sub: "Domina la fotografia con telefono primero — es la forma mas rapida y barata de enamorarte del oficio. Pero en algun momento tu ojo superara lo que la IA computacional puede entregar. Aqui estan los 5 escenarios donde una DSLR o mirrorless real supera dramaticamente al mejor telefono, y que hacer despues.",
    upgrade_scenarios: [
      {
        scene: "Baja luz extrema y astrofotografia",
        why: "Los sensores de telefono son fisicamente diminutos (~1/1.3 pulgada en un Pixel Pro). Un sensor full-frame de DSLR es ~30x mas grande. Esa area de sensor capta ~30x mas luz por toma — haciendo posibles estrellas, Via Lactea, conciertos tenues y deportes en interior de noche SIN la reduccion de ruido por IA que embarra todo.",
      },
      {
        scene: "Telefoto de fauna y deportes",
        why: "El lente fisico mas largo de un telefono llega a 5x optico (~120mm equivalente). Una DSLR de gama media + 100-400mm alcanza 400mm nativos — 8x mas alcance SIN perdida de calidad. Para aves, atletas lejanos o animales esquivos, esta brecha es imposible de cerrar.",
      },
      {
        scene: "Impresiones grandes de bellas artes",
        why: "Los telefonos producen excelentes impresiones 4x6 y 8x10. Pero una impresion de galeria de 60cm expone cada bit del suavizado por IA, cada esquina embarrada del ultra-gran-angular, cada artefacto de Modo Noche. Las camaras reales disparan archivos reales de 24-45 MP que aguantan ampliacion a tamaño de cartel.",
      },
      {
        scene: "Profundidad de campo realmente poca (bokeh f/1.4 real)",
        why: "El Modo Retrato FALSIFICA el bokeh con IA. Un vidrio f/1.4 real sobre un cuerpo full-frame crea desenfoque de fondo fisico, perfecto, sin errores de borde — incluso en lentes, pelo fino y fondos ocupados donde el Modo Retrato colapsa. Una vez que ves bokeh real, la version computacional se ve plastica.",
      },
      {
        scene: "Rastreo deportivo y accion rapida",
        why: "Las mirrorless modernas (Sony A9 III, Canon R3, Nikon Z9) disparan 20-40 fps con IA de rastreo de sujeto que sigue el ojo del atleta por todo el cuadro. Los telefonos topan ~30 fps SIN rastreo de enfoque continuo. Para surfistas, autos de carrera, basquetbol o cualquier cosa que se mueve rapido y erratico, es noche-y-dia.",
      },
    ],
    upgrade_cta_title: "Tus siguientes pasos",
    upgrade_cta_steps: [
      { title: "1. Usa el Asesor de Ajustes DSLR", desc: "75+ modelos de camara soportados — elige el cuerpo que estas considerando y explora recomendaciones reales de exposicion para las escenas que quieres disparar.", to: "/", btn: "Abrir Asesor" },
      { title: "2. Lee 'Decision de Cuerpo de Camara' en Carreras", desc: "Que separa cuerpos de entrada, gama media y profesionales — y cual tiene sentido para TU estilo.", to: "/careers", btn: "Leer Carreras" },
      { title: "3. Planea tu kit con Mi Equipo", desc: "La herramienta de inventario te deja rastrear camara + lente + iluminacion + accesorios con precios, numeros de serie y exportacion a Excel para seguro.", to: "/gear", btn: "Abrir Mi Equipo" },
      { title: "4. Visita la Biblioteca de Planos", desc: "Cada proyecto que probaste en telefono, planeado con una camara real + ajustes recomendados. Ve lo que es posible cuando cruzas ese puente.", to: "/shots", btn: "Ver Planos" },
    ],
    upgrade_closing: "Telefono → DSLR es la progresion mas natural en fotografia. Ya tienes el OJO. Una camara real solo quita el techo tecnico.",

    projects_title: "10 Proyectos de Practica — Dispara, Auto-Critica, Comparte",
    projects_sub: "Ahora aplica lo aprendido. Cada proyecto esta diseñado para smartphone e incluye la tecnica a practicar, ajustes exactos de inicio, 5 tips, entregables, y una lista de auto-critica para que tu (o un amigo) revisen el resultado honestamente.",
    difficulty_label: "Dificultad",
    objective_label: "Objetivo",
    settings_label: "Ajustes Iniciales",
    tips_label: "Tips",
    deliverables_label: "Entregables",
    critique_label: "Lista de Auto-Critica",
    diff_beginner: "Principiante",
    diff_intermediate: "Intermedio",
    diff_advanced: "Avanzado",

    projects: [
      {
        id: "phone_food",
        icon: "ForkKnife",
        title: "1. Fotografia de Alimentos (Luz de Ventana)",
        desc: "Fotografia una comida usando solo luz de ventana — practica composicion y tap-para-enfocar en pantalla pequeña.",
        difficulty: "beginner",
        relates: "Leccion 1 (Exposicion y Enfoque), Leccion 3 (Composicion)",
        objective: "5 imagenes de un plato desde diferentes angulos (cenital, 45 grados, nivel de ojo) usando SOLO luz de ventana.",
        settings: "Lente principal 1x | Cuadricula activada | Toca el sujeto para enfocar → desliza el sol-slider HACIA ABAJO ligeramente para proteger altas luces | Dispara RAW si esta disponible",
        tips: [
          "Coloca el plato a 60-90cm de una ventana grande. La luz debe pegar al plato de lado, no detras de ti.",
          "Usa una servilleta blanca o plato de papel como reflector de relleno en el lado de sombra.",
          "Toca la parte mas brillante del plato para exponer por ella — luego recompon.",
          "Prueba primero el angulo 'humano comiendo' a 45 grados, luego cenital (flat lay), luego nivel de ojo para toma hero.",
          "Evita las luces del techo del cuarto — hacen ver la comida naranja/amarilla. Solo luz de ventana.",
        ],
        deliverables: "5 imagenes editadas: 1 cenital flat-lay, 2 a 45°, 1 nivel de ojo hero, 1 detalle close-up.",
        critique: [
          "Se ve la comida APETITOSA (no plana ni sombria)?",
          "Las altas luces sobre la comida estan preservadas (no quemadas a blanco)?",
          "Esta el plato en enfoque nitido (especialmente el borde mas cercano)?",
          "Recortaste con apretura — sin desorden distractor en el cuadro?",
          "La regla de tercios coloco el plato fuera del centro, no justo al medio?",
        ],
      },
      {
        id: "phone_portrait",
        icon: "User",
        title: "2. Retrato — 3 Estilos, Misma Persona",
        desc: "Tres retratos distintos usando lente principal, telefoto, Y Modo Retrato.",
        difficulty: "beginner",
        relates: "Leccion 4 (Seleccion de Lentes), Leccion 5 (Modo Retrato)",
        objective: "Produce 3 retratos de la MISMA persona mostrando moods diferentes + diferentes lentes.",
        settings: "Retrato 1: 1x principal | Retrato 2: telefoto 2x o 3x | Retrato 3: Modo Retrato a 2x | Toca-y-manten para bloqueo AE/AF en el ojo",
        tips: [
          "Dispara en sombra abierta (no sol directo en la cara — entrecerrar ojos y sombras duras).",
          "Enfoca en el ojo mas cercano a la camara. Toca y manten sobre el ojo hasta que aparezca Bloqueo AE/AF.",
          "Para Modo Retrato: manten 60cm-2.4m. Observa halos en orejas/lentes y ajusta si hace falta.",
          "El telefoto (2x/3x) aplana facciones = mas favorecedor que el 1x para retratos cerrados.",
          "Tras capturar, en Modo Retrato ve Editar → ajusta el slider de apertura al gusto.",
        ],
        deliverables: "3 retratos: 1 headshot cerrado (telefoto), 1 ambiental (1x con entorno), 1 con bokeh computado de Modo Retrato.",
        critique: [
          "Los ojos estan NAVAJA-nitidos? (zoom al 100%)",
          "El tono de piel es natural — no naranja, verde ni magenta?",
          "El Modo Retrato manejo limpio el pelo y los lentes, o hay errores obvios de halo?",
          "Usaste el telefoto para el headshot cerrado (NO el gran angular)?",
          "Cada uno de los 3 retratos se siente DIFERENTE en mood o perspectiva?",
        ],
      },
      {
        id: "phone_landscape",
        icon: "Mountains",
        title: "3. Paisaje en Golden Hour (Ultra Gran Angular)",
        desc: "Captura un paisaje en golden hour usando el lente .5x ultra-gran-angular de tu telefono.",
        difficulty: "beginner",
        relates: "Leccion 4 (Lentes), Leccion 6 (RAW)",
        objective: "5 imagenes de paisaje durante golden hour usando SOLO el lente .5x ultra-gran-angular. Captura RAW obligatoria.",
        settings: "Lente ultra-gran-angular .5x | Cuadricula activada | Formato RAW (.DNG) activado | Toca para enfocar en un elemento de mediofondo | Apoyate contra algo solido (rocas, barandal) para estabilidad",
        tips: [
          "Llega 30 min antes del atardecer para explorar. La golden hour dura ~45 min maximo.",
          "Incluye un elemento de primer plano FUERTE a 30cm-1m del lente — flores, rocas, agua. El ultra-gran-angular exagera el primer plano.",
          "Coloca el horizonte en el tercio inferior (dominio cielo) O tercio superior (dominio tierra). Nunca al centro.",
          "Toca para enfocar 1/3 dentro de la escena. Desliza exposicion HACIA ABAJO ligeramente para proteger el cielo brillante.",
          "Edita el RAW DNG en Lightroom Mobile: levanta sombras +30, baja altas luces -30, sube claridad.",
        ],
        deliverables: "5 paisajes editados: 2 vistas amplias, 1 con primer plano fuerte, 1 con lineas guia (camino, rio), 1 vertical 9:16 para Stories.",
        critique: [
          "El horizonte esta nivelado (usaste la cuadricula + indicador de nivel)?",
          "Las altas luces del cielo estan preservadas (no blanco puro)?",
          "Hay un elemento de primer plano claro dando profundidad?",
          "Evitaste la trampa del horizonte al centro?",
          "EDITASTE el RAW, o solo exportaste el JPG? Las ediciones reales se ven en sombras y color.",
        ],
      },
      {
        id: "phone_street",
        icon: "PersonSimpleWalk",
        title: "4. Fotografia Callejera (Candida)",
        desc: "Captura 5 momentos candidos de extraños en un espacio publico. La invisibilidad del telefono es un superpoder aqui.",
        difficulty: "intermediate",
        relates: "Leccion 1 (Exposicion), Leccion 4 (Lentes)",
        objective: "5 fotos callejeras candidas mostrando momentos humanos reales — sin tomas posadas.",
        settings: "Lente principal 1x O telefoto 2x | Bloqueo AE/AF en una pared de tono medio ANTES de levantar el telefono | Modo rafaga para sujetos en movimiento",
        tips: [
          "Pre-bloquea la exposicion en una pared de tono medio bajo la misma luz que tu sujeto. Luego levanta el telefono, encuadra, dispara — sin retraso de medicion = sin momento perdido.",
          "Sosten el telefono a altura del pecho (no de la cara) para disparar de forma menos obvia.",
          "El telefoto (2x/3x) te deja disparar desde el otro lado de la calle — mucho menos invasivo.",
          "Busca capas: persona en primer plano + evento en mediofondo + contexto al fondo.",
          "Respeta la ley: en la mayoria de lugares es legal fotografiar en espacios publicos, pero sigue normas locales y nunca acoses a nadie.",
        ],
        deliverables: "5 imagenes callejeras candidas: al menos 2 con personas, 1 con sombras o patrones de luz fuertes, 1 detalle arquitectonico, 1 reflejo o escena con capas.",
        critique: [
          "Las fotos se sienten CANDIDAS (gente haciendo cosas reales, no posando)?",
          "El momento esta capturado en el pico de la accion / expresion?",
          "Evitaste cortes incomodos en articulaciones (codos, rodillas)?",
          "La exposicion es consistente en todo el set (usaste pre-bloqueo AE)?",
          "Un extraño viendo este set entenderia la UBICACION y el MOOD?",
        ],
      },
      {
        id: "phone_lowlight",
        icon: "Moon",
        title: "5. Baja Luz / Modo Noche",
        desc: "Lleva la IA computacional de tu telefono al limite — dispara 5 escenas despues del anochecer usando Modo Noche.",
        difficulty: "intermediate",
        relates: "Leccion 2 (Modo Pro), Leccion 6 (RAW)",
        objective: "5 imagenes nocturnas: 2 cityscape, 2 retrato, 1 larga exposicion de luces en movimiento.",
        settings: "iPhone: Modo Noche se activa automaticamente en baja luz, manten quieto 1-3 segundos | Android: Pixel 'Night Sight', Samsung 'Noche' | Apoya el telefono contra pared o barandal para estabilidad",
        tips: [
          "El Modo Noche toma 3-10 segundos. DEBES sostener el telefono PERFECTAMENTE quieto — apoyalo contra algo solido.",
          "Bloquea exposicion ANTES de que se active Modo Noche: toca y manten en la parte mas brillante de la escena.",
          "Para estelas de luz (autos, trafico), usa Modo Pro con 4-15 segundos de velocidad sobre tripode o apoyo solido.",
          "Para retratos nocturnos, haz que el sujeto se quede quieto 2-3 segundos mientras Modo Noche procesa.",
          "Evita el 1x al maximo zoom digital — el ruido destruye la toma. Solo lentes opticos de noche.",
        ],
        deliverables: "5 imagenes nocturnas: 2 cityscapes, 2 retratos nocturnos, 1 estela de luz o escena estelar.",
        critique: [
          "La imagen esta NITIDA (no borrosa por movimiento)?",
          "El ruido / grano es aceptable, o sobre-zoomeaste?",
          "Las altas luces de los faroles estan preservadas (no manchas brillantes)?",
          "El retrato nocturno congelo al sujeto (no movido)?",
          "Se siente como noche (no sobre-iluminado hasta parecer dia)?",
        ],
      },
      {
        id: "phone_macro",
        icon: "MagnifyingGlassPlus",
        title: "6. Macro / Close-Up",
        desc: "Usa el modo macro de tu telefono (o acercate a 5 cm) para capturar detalle invisible al ojo desnudo.",
        difficulty: "intermediate",
        relates: "Leccion 1 (Enfoque), Leccion 4 (Lentes)",
        objective: "5 imagenes macro: una flor, una superficie texturizada, un insecto o detalle de comida, gotas de agua, y un objeto creativo cotidiano.",
        settings: "iPhone 13 Pro+: Macro se engancha automaticamente a 14 cm | Otros telefonos: instala una app macro gratuita o usa el ultra-gran-angular a distancia minima de enfoque | Toca PRECISAMENTE el punto que quieres nitido | Modo rafaga ayuda",
        tips: [
          "La PDC es muy delgada en macro. Enfoca precisamente en el detalle mas importante (ojo de insecto, centro de flor).",
          "Usa luz natural, nunca el LED del telefono — el flash crea sombras duras horribles a corta distancia.",
          "Apoya los codos o descansa el telefono en algo. Hasta el menor movimiento destruye la nitidez macro.",
          "Rocia gotas de agua sobre flores u hojas con un atomizador para drama añadido.",
          "Edita agresivo: las tomas macro se benefician con +claridad, +textura, y nitidez selectiva en el punto de enfoque.",
        ],
        deliverables: "5 fotos macro mostrando: 1 flor, 1 textura, 1 ser vivo O comida, 1 gota de agua, 1 objeto creativo.",
        critique: [
          "El PUNTO FOCAL (ojo de insecto, estambre, gota) esta navaja-nitido?",
          "La poca PDC dirige el ojo del espectador al sujeto?",
          "El fondo esta limpio / no distrae?",
          "Los colores y texturas son vivos, no embarrados?",
          "Un espectador podria adivinar QUE ES el sujeto sin contexto?",
        ],
      },
      {
        id: "phone_product",
        icon: "Package",
        title: "7. Fotografia de Producto (Estilo E-Commerce)",
        desc: "Fotografia 3 productos pequeños para una tienda online. Nitido, color exacto, fondo limpio.",
        difficulty: "intermediate",
        relates: "Leccion 2 (Modo Pro + WB), Leccion 6 (RAW)",
        objective: "9 fotos total (3 productos × 3 angulos cada uno): hero shot, angulo 3/4, y detalle close-up.",
        settings: "Modo Pro | WB Manual a ~5500K (luz dia) | ISO 50-100 | f/2.8 (o solo el lente principal) | Tripode o apoyo solido obligatorio | Formato RAW",
        tips: [
          "Usa una cartulina blanca o papel de impresora como fondo Y reflector de relleno.",
          "Dispara en luz de ventana suave (mañana temprano o tarde). Evita sol directo.",
          "Configura WB Manual — no confies en Auto WB en una sesion limpia de producto.",
          "Usa telefoto (2x/3x) para menos distorsion de perspectiva en objetos pequeños.",
          "Edita el RAW: quita motas de polvo, corrige WB precisa con el cuentagotas, afila bordes del producto.",
        ],
        deliverables: "9 fotos editadas: 3 hero, 3 angulo, 3 detalle — todas con fondo blanco y color consistente.",
        critique: [
          "El color del producto es EXACTO (coincide con el color real)?",
          "El fondo es blanco puro o casi-blanco (usa el muestreador en Lightroom)?",
          "Los reflejos / hot spots estan controlados, no distractores?",
          "El producto esta navaja-nitido donde importa?",
          "REALMENTE usarias estas en un listing de Shopify o Etsy?",
        ],
      },
      {
        id: "phone_action",
        icon: "Football",
        title: "8. Accion / Deportes (Rafaga)",
        desc: "Captura un sujeto en movimiento — niños jugando, mascota corriendo, skater, partido deportivo.",
        difficulty: "intermediate",
        relates: "Leccion 1 (Enfoque), Leccion 4 (Lente)",
        objective: "5 imagenes con accion congelada usando Modo Rafaga y telefoto.",
        settings: "Telefoto (2x/3x) para alcance | Manten el disparador para rafaga (hasta 30+ cuadros) | Bloqueo AE/AF en la ruta del sujeto ANTES de empezar | Exterior con luz brillante es lo mas facil",
        tips: [
          "Pre-enfoca en el punto donde pasara la accion, luego dispara rafaga cuando llegue.",
          "Luz dia brillante = velocidades rapidas → movimiento congelado. Deportes en interior son MUCHO mas dificiles.",
          "Telefoto + rafaga = el superpoder deportivo de un telefono. El 1x es muy ancho para accion lejana.",
          "Rastrea al sujeto durante la rafaga — no solo spray-and-pray.",
          "De una rafaga de 30 cuadros, elige 1-2 con expresion / accion pico. Borra el resto.",
        ],
        deliverables: "5 mejores tomas de rafaga: 2 contexto amplio, 3 accion pico cerrada.",
        critique: [
          "La accion esta CONGELADA (sin movimiento borroso en el sujeto)?",
          "El sujeto esta nitido — ojos / pelota / punto focal en enfoque?",
          "Elegiste el momento PICO de cada rafaga, no un cuadro intermedio?",
          "La composicion es fuerte (sujeto no cortado en articulaciones)?",
          "La foto cuenta una historia o transmite energia?",
        ],
      },
      {
        id: "phone_creative",
        icon: "Sparkle",
        title: "9. Serie Creativa / Auto-Retratos",
        desc: "Usa la camara frontal, espejos, reflejos y angulos creativos para hacer 5 AUTO-RETRATOS que no se vean como selfies.",
        difficulty: "advanced",
        relates: "Leccion 3 (Composicion), Leccion 5 (Modo Retrato)",
        objective: "5 auto-retratos con tecnicas diferentes — al menos uno usando espejo, uno con Modo Retrato, uno con sombra larga o silueta.",
        settings: "Camara frontal O trasera (mejor calidad) con temporizador | Tripode, gimbal o superficie estable | Self-timer 10s + Rafaga | Cuadricula activada",
        tips: [
          "La camara TRASERA siempre tiene mejor calidad que la frontal. Usa timer + tripode para un auto-retrato pro.",
          "Los espejos duplican tu composicion. Disparate reflejado en vidrio de ventana, agua de lago o espejo de auto.",
          "Siluetas: ponte frente a un cielo/ventana brillante. Subexpon -2 EV.",
          "Sombras largas en golden hour: dispara tu propia sombra en pared o piso desde angulo bajo.",
          "Se creativo con espejos, encuadres a traves de objetos, puertas, hojas, etc. Evita el obvio selfie del baño.",
        ],
        deliverables: "5 auto-retratos, cada uno con tecnica diferente. Añade caption de 1 linea explicando tu concepto en cada uno.",
        critique: [
          "Cada foto se siente DIFERENTE — diferente mood, tecnica o idea visual?",
          "Evitaste el cliche 'selfie del espejo del baño'?",
          "TU estas en FOCO (no el marco del espejo o el fondo)?",
          "La iluminacion es favorecedora o intencional?",
          "Un extraño podria adivinar tu CONCEPTO en cada foto?",
        ],
      },
      {
        id: "phone_series",
        icon: "ImagesSquare",
        title: "10. Historia Visual — 24 Horas en Mi Vida",
        desc: "Proyecto culminante. Documenta un solo dia con 10 fotos de telefono que formen una narrativa visual coherente.",
        difficulty: "advanced",
        relates: "Todas las 6 lecciones combinadas",
        objective: "10 fotos cronicando 24 horas de tu vida. Debe incluir: mañana, mediodia, tarde y noche. Diferentes lentes, modos y moods.",
        settings: "Aplica TODO lo aprendido: tap-enfoque + bloqueo AE/AF para escenas de alto contraste, RAW para tomas importantes, Modo Retrato para 1-2, Modo Pro para noche, telefoto para candidos, ultra-gran-angular para entornos.",
        tips: [
          "Planea un arco narrativo: despertar → rutina → actividad exterior → comida → tarde → noche.",
          "Mezcla tomas amplias de entorno con close-ups intimos de detalle para ritmo visual.",
          "Incluye AL MENOS una toma de cada una de las 6 lecciones: enfoque/exposicion, Modo Pro, composicion (cuadricula), variedad de lentes, Modo Retrato, edicion RAW.",
          "Edita consistente — aplica el mismo tratamiento de color a las 10 en Lightroom Mobile para un look cohesivo.",
          "Capciona cada foto con una oracion. Los captions forman una narrativa escrita junto a la visual.",
        ],
        deliverables: "10 fotos editadas en orden cronologico + reflexion de 1 parrafo sobre lo que aprendiste de tu dia Y de la fotografia movil.",
        critique: [
          "Las 10 fotos cuentan un arco narrativo CLARO (mañana a noche)?",
          "Hay variedad visual (ancho / cerrado, claro / oscuro, retratos / detalles)?",
          "Usaste AL MENOS 4 tecnicas diferentes de las 6 lecciones?",
          "La edicion se siente CONSISTENTE en las 10 (mismo tono de color, mismo aspect ratio si aplica)?",
          "Si alguien navega esto sin captions, ENTENDERIA tu dia?",
        ],
      },
    ],
  },
};

const ICONS = {
  Hand,
  Sliders,
  GridFour,
  ArrowsHorizontal,
  UserCircle,
  FileImage,
  ForkKnife,
  User,
  Mountains,
  PersonSimpleWalk,
  Moon,
  MagnifyingGlassPlus,
  Package,
  Football,
  Sparkle,
  ImagesSquare,
};

const DIFF_BADGE = {
  beginner: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  intermediate: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  advanced: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function ProjectCard({ project, c }) {
  const Icon = ICONS[project.icon] || Sparkle;
  const diffLabel = c[`diff_${project.difficulty}`] || project.difficulty;
  return (
    <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-3" data-testid={`mobile-project-${project.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
          <h3 className="font-['Outfit'] text-white text-lg font-medium">{project.title}</h3>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${DIFF_BADGE[project.difficulty]}`}>
          {diffLabel}
        </span>
      </div>
      <p className="text-[#A1A1AA] text-sm leading-relaxed">{project.desc}</p>
      <p className="text-[#52525B] text-xs italic">→ {project.relates}</p>

      <div className="grid sm:grid-cols-2 gap-3 mt-2">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Target weight="fill" className="w-3.5 h-3.5 text-[#F5A524]" />
            <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold">{c.objective_label}</p>
          </div>
          <p className="text-[#A1A1AA] text-xs leading-relaxed">{project.objective}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
          <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-1.5">{c.settings_label}</p>
          <p className="text-[#A1A1AA] text-xs leading-relaxed font-mono">{project.settings}</p>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
        <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-1.5">{c.tips_label}</p>
        <ol className="space-y-1">
          {project.tips.map((t, i) => (
            <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
              <span className="text-[#F5A524] font-bold shrink-0">{i + 1}.</span>
              <span>{t}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
        <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-1.5">{c.deliverables_label}</p>
        <p className="text-[#A1A1AA] text-xs leading-relaxed">{project.deliverables}</p>
      </div>

      <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-400" />
          <p className="text-emerald-300 text-xs uppercase tracking-widest font-semibold">{c.critique_label}</p>
        </div>
        <ul className="space-y-1">
          {project.critique.map((q, i) => (
            <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
              <span className="text-emerald-400 shrink-0">☐</span>
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

function LessonCard({ section, c }) {
  const Icon = ICONS[section.icon] || Hand;
  return (
    <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-4" data-testid={`mobile-section-${section.id}`}>
      <div className="flex items-center gap-2">
        <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
        <h2 className="font-['Outfit'] text-white text-xl font-medium">{section.title}</h2>
      </div>
      <p className="text-[#A1A1AA] text-sm leading-relaxed">{section.intro}</p>

      {/* iOS / Android side-by-side on desktop, stacked on mobile */}
      <div className="grid md:grid-cols-2 gap-3">
        {/* iOS card */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2.5">
            <AppleLogo weight="fill" className="w-4 h-4 text-[#F5A524]" />
            <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold">{c.ios_label}</p>
          </div>
          <ol className="space-y-1.5">
            {section.ios_steps.map((s, i) => (
              <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
                <span className="text-[#F5A524] font-bold shrink-0">{i + 1}.</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Android card */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2.5">
            <AndroidLogo weight="fill" className="w-4 h-4 text-emerald-400" />
            <p className="text-emerald-400 text-xs uppercase tracking-widest font-semibold">{c.android_label}</p>
          </div>
          <ol className="space-y-1.5">
            {section.android_steps.map((s, i) => (
              <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
                <span className="text-emerald-400 font-bold shrink-0">{i + 1}.</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Pro tip ribbon */}
      <div className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-lg p-3">
        <p className="text-[#F5A524] text-xs italic leading-relaxed">
          <span className="font-semibold uppercase tracking-widest mr-1.5">{c.pro_tip_label}:</span>
          {section.tip}
        </p>
      </div>
    </motion.div>
  );
}

export default function SmartphonePhotographyPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(printRef, () => `smartphone-photography-${lang}.pdf`, [lang]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black" data-testid="smartphone-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <DeviceMobile weight="fill" className="w-6 h-6 text-[#F5A524]" />
                <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight">{c.page_title}</h1>
              </div>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="smartphone-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Quick jump grid — sub-nav of all 6 lessons + projects */}
          <motion.div variants={itemAnim} className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="smartphone-subnav">
            {c.sections.map((s) => {
              const Icon = ICONS[s.icon] || Hand;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="bg-[#121214]/60 backdrop-blur-md border border-white/10 hover:border-[#F5A524]/40 rounded-lg p-3 flex items-start gap-2 transition-colors group"
                  data-testid={`smartphone-jump-${s.id}`}
                >
                  <Icon weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
                  <span className="text-[#A1A1AA] group-hover:text-white text-xs leading-snug transition-colors">{s.title}</span>
                </a>
              );
            })}
            <a
              href="#projects"
              className="bg-[#F5A524]/10 backdrop-blur-md border border-[#F5A524]/40 hover:border-[#F5A524] rounded-lg p-3 flex items-start gap-2 transition-colors group sm:col-span-3"
              data-testid="smartphone-jump-projects"
            >
              <Sparkle weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
              <span className="text-[#F5A524] group-hover:text-white text-xs leading-snug font-semibold transition-colors">{c.projects_title}</span>
            </a>
          </motion.div>

          {/* Lessons */}
          {c.sections.map((s) => (
            <div key={s.id} id={s.id} className="scroll-mt-20">
              <LessonCard section={s} c={c} />
            </div>
          ))}

          {/* Projects header */}
          <motion.div variants={itemAnim} id="projects" className="scroll-mt-20 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle weight="fill" className="w-6 h-6 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-2xl sm:text-3xl font-semibold tracking-tight">{c.projects_title}</h2>
            </div>
            <p className="text-[#71717A] text-sm max-w-3xl">{c.projects_sub}</p>
          </motion.div>

          {/* Projects grid */}
          {c.projects.map((p) => (
            <ProjectCard key={p.id} project={p} c={c} />
          ))}

          {/* Closing */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6">
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.closing}</p>
          </motion.div>

          {/* Upgrade-path card */}
          <motion.div variants={itemAnim} className="bg-gradient-to-br from-[#F5A524]/10 via-[#121214]/80 to-[#121214]/80 backdrop-blur-md border-2 border-[#F5A524]/40 rounded-xl p-6 sm:p-8 space-y-5" data-testid="smartphone-upgrade-card">
            <div className="flex items-center gap-2">
              <Rocket weight="fill" className="w-6 h-6 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-2xl sm:text-3xl font-semibold tracking-tight">{c.upgrade_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed max-w-3xl">{c.upgrade_sub}</p>

            {/* 5 scenario cards */}
            <div className="grid sm:grid-cols-2 gap-3">
              {c.upgrade_scenarios.map((s, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                  <p className="text-[#F5A524] font-medium text-sm mb-1.5">{s.scene}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{s.why}</p>
                </div>
              ))}
            </div>

            {/* CTA grid → 4 internal links */}
            <div>
              <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-3">{c.upgrade_cta_title}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {c.upgrade_cta_steps.map((step, i) => (
                  <Link
                    key={i}
                    to={step.to}
                    className="bg-[#0A0A0A] hover:bg-[#F5A524]/10 border border-white/10 hover:border-[#F5A524]/40 rounded-lg p-4 group transition-all"
                    data-testid={`smartphone-upgrade-cta-${i}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-white text-sm font-medium leading-tight group-hover:text-[#F5A524] transition-colors">{step.title}</p>
                      <ArrowUpRight weight="bold" className="w-4 h-4 text-[#F5A524] shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                    <p className="text-[#A1A1AA] text-xs leading-relaxed mb-2">{step.desc}</p>
                    <span className="text-[#F5A524] text-xs font-semibold">{step.btn} →</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Closing line */}
            <div className="bg-[#F5A524]/15 border border-[#F5A524]/40 rounded-lg p-4">
              <p className="text-[#F5A524] text-sm leading-relaxed italic font-medium text-center">{c.upgrade_closing}</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Hidden printable version */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>
        {c.sections.map((s, i) => (
          <div key={s.id} className="mb-5 break-inside-avoid">
            <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{s.title}</h2>
            <p className="text-sm text-gray-700 mb-2">{s.intro}</p>
            <p className="text-sm font-bold text-gray-800">{c.ios_label}</p>
            <ol className="list-decimal ml-5 space-y-1 mb-2">
              {s.ios_steps.map((st, j) => <li key={j} className="text-xs text-gray-700">{st}</li>)}
            </ol>
            <p className="text-sm font-bold text-gray-800">{c.android_label}</p>
            <ol className="list-decimal ml-5 space-y-1 mb-2">
              {s.android_steps.map((st, j) => <li key={j} className="text-xs text-gray-700">{st}</li>)}
            </ol>
            <p className="text-xs text-gray-700 italic"><strong>{c.pro_tip_label}:</strong> {s.tip}</p>
          </div>
        ))}
        <p className="text-sm text-gray-700 italic mt-4">{c.closing}</p>

        <h1 className="text-2xl font-bold text-center mt-8 mb-1 border-t-2 border-gray-300 pt-4">{c.projects_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.projects_sub}</p>
        {c.projects.map((p) => (
          <div key={p.id} className="mb-5 break-inside-avoid">
            <h2 className="text-lg font-bold mb-1 border-b-2 border-gray-200 pb-1">
              {p.title} <span className="text-xs italic text-gray-500">— {c[`diff_${p.difficulty}`]}</span>
            </h2>
            <p className="text-sm text-gray-700 mb-1">{p.desc}</p>
            <p className="text-xs italic text-gray-500 mb-2">→ {p.relates}</p>
            <p className="text-xs text-gray-700 mb-1"><strong>{c.objective_label}:</strong> {p.objective}</p>
            <p className="text-xs text-gray-700 mb-1"><strong>{c.settings_label}:</strong> {p.settings}</p>
            <p className="text-xs font-bold text-gray-800">{c.tips_label}:</p>
            <ol className="list-decimal ml-5 space-y-0.5 mb-1">
              {p.tips.map((t, i) => <li key={i} className="text-xs text-gray-700">{t}</li>)}
            </ol>
            <p className="text-xs text-gray-700 mb-1"><strong>{c.deliverables_label}:</strong> {p.deliverables}</p>
            <p className="text-xs font-bold text-gray-800">{c.critique_label}:</p>
            <ul className="ml-5 space-y-0.5 mb-2">
              {p.critique.map((q, i) => <li key={i} className="text-xs text-gray-700">☐ {q}</li>)}
            </ul>
          </div>
        ))}

        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>

        <h1 className="text-2xl font-bold text-center mt-8 mb-1 border-t-2 border-gray-300 pt-4">{c.upgrade_title}</h1>
        <p className="text-center text-gray-500 mb-4 text-sm">{c.upgrade_sub}</p>
        {c.upgrade_scenarios.map((s, i) => (
          <div key={i} className="mb-2">
            <p className="text-sm font-bold">{s.scene}</p>
            <p className="text-xs text-gray-700">{s.why}</p>
          </div>
        ))}
        <p className="text-sm font-bold mt-3">{c.upgrade_cta_title}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-3">
          {c.upgrade_cta_steps.map((step, i) => (
            <li key={i} className="text-xs text-gray-700"><strong>{step.title}:</strong> {step.desc}</li>
          ))}
        </ol>
        <p className="text-xs text-gray-700 italic">{c.upgrade_closing}</p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}